// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { EventEmitter } from 'events';
import { ConnectionAttemptResponse } from '@shared/commons/ConnectionAttemptResponse';
import { ConnectionErrorsEnum } from '@shared/commons/ConnectionEnums';
import { ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { Resources } from '@shared/localizations/Resources';
import { ConectionResultEnum, ConnectionStates } from '@shared/utils/Connections/connection.types';
import { CustomEvent } from '@shared/utils/CustomEvents';
import { Message } from '@shared/utils/DirectMessages/Message';
import { ConnnectionResponseCode } from '@shared/utils/Enums/ConnectionEnum';
import { PFSVendor } from '@shared/vendors/PFSVendor/PFSVendor';
class _Connection {
    constructor(dataCache, funcBeforeDisconnectCallback) {
        this.vendor = null;
        this.vendorMsgListener = null;
        this.connectionState = ConnectionStates.DISCONNECTED;
        this.onConnectProcessError = new CustomEvent();
        this.onGetToken = new CustomEvent();
        this.ConnectResultData = null;
        this.Reconnecting = false;
        this.RefreshAccessTokenInterval = null;
        this._eventEmitter = new EventEmitter();
        this._localDataProvider = null;
        this.OnVendorDisconnected = (disconectArguments) => __awaiter(this, void 0, void 0, function* () {
            let forseConnectionStateChange = !isNullOrUndefined(disconectArguments)
                ? disconectArguments.ErrorInConnection
                : false;
            if (!isNullOrUndefined(disconectArguments)) {
                if (disconectArguments.LogoutType === 1 &&
                    (this.connectionState === ConnectionStates.CONNECTION_LOST ||
                        this.connectionState === ConnectionStates.CONNECTED)) {
                    this.ClearPreviousVendorRefs();
                    yield this.OnConnectionLost();
                    return;
                }
                else if (!isNullOrUndefined(disconectArguments.verificationMode)) {
                    this.lastConnectionAttemptResponse = {
                        text: '',
                        connectReason: ConectionResultEnum.AdditionalVerification,
                        verificationModeCode: disconectArguments.verificationMode,
                        UserID: disconectArguments.UserID,
                        needVerificationPassword: disconectArguments.needVerificationPassword
                    };
                    yield this.SetConnectionState(ConnectionStates.DISCONNECTED, true);
                }
                else {
                    switch (disconectArguments.ErrorCode) {
                        case ConnectionErrorsEnum.ERROR_MULTI_POLICY:
                            this.onConnectProcessError.Raise(new ConnectionAttemptResponse(disconectArguments.Text, ConnectionErrorsEnum.ERROR_MULTI_POLICY));
                            this._eventEmitter.emit('errorMultiPolicy', disconectArguments);
                            forseConnectionStateChange = true;
                            break;
                        case ConnectionErrorsEnum.ERROR_NEED_ACTIVATION:
                            this.lastConnectionAttemptResponse = {
                                text: disconectArguments.Text,
                                connectReason: ConectionResultEnum.NeedActivation
                            };
                            break;
                        case ConnectionErrorsEnum.INVALID_BRANDING_KEY:
                            this.lastConnectionAttemptResponse = {
                                text: disconectArguments.Text,
                                ErrorCode: disconectArguments.ErrorCode,
                                connectReason: ConectionResultEnum.InvalidBrandingKey
                            };
                            break;
                        case ConnectionErrorsEnum.MAX_CONCURRENT_CONNECTIONS:
                            this.onConnectProcessError.Raise(new ConnectionAttemptResponse(disconectArguments.Text, ConnectionErrorsEnum.MAX_CONCURRENT_CONNECTIONS));
                            forseConnectionStateChange = true;
                            break;
                        default:
                            if (!isNullOrUndefined(disconectArguments.Text)) {
                                this.lastConnectionAttemptResponse = {
                                    text: disconectArguments.Text,
                                    ErrorCode: disconectArguments.ErrorCode
                                };
                            }
                    }
                    yield this.SetConnectionState(ConnectionStates.DISCONNECTED, forseConnectionStateChange);
                }
            }
            else {
                yield this.SetConnectionState(ConnectionStates.DISCONNECTED, forseConnectionStateChange);
            }
            this.ClearPreviousVendorRefs();
        });
        this.dataCache = dataCache;
        this.lastConnectionAttemptResponse = { text: '' };
        this.funcBeforeDisconnectCallback = funcBeforeDisconnectCallback;
    }
    connect(connectParams_1) {
        return __awaiter(this, arguments, void 0, function* (connectParams, fromReconnect = false) {
            const vendor = this.prepareVendor();
            this.lastConnectionAttemptResponse = { text: '' };
            if (!this.Reconnecting) {
                const connected = yield this._connectProcess(vendor, connectParams);
                return { connected };
            }
            const connectResultData = yield this.RefreshAccessToken(fromReconnect);
            if (!this.Reconnecting) {
                return { connected: true };
            }
            if (connectResultData.Valid && connectResultData.StreamOpened) {
                const connected = yield this._connectProcess(vendor, connectParams);
                return { connected };
            }
            if (!connectResultData.Valid && connectResultData.StreamOpened) {
                this.lastConnectionAttemptResponse = {
                    text: Resources.getResource('Session has expired. Please login.')
                };
                this.disconnect();
                return { connected: false };
            }
            if (fromReconnect && !connectResultData.StreamOpened) {
                return { connected: false, tryAgain: true };
            }
            return { connected: false };
        });
    }
    prepareVendor() {
        if (!this.Reconnecting) {
            this.ConnectResultData = null;
        }
        const vendor = this.createPFSVendor(this.dataCache.ThirdPartyProvidersValidator);
        if (this.vendor) {
            this.ClearPreviousVendorRefs();
        }
        this.vendor = vendor;
        if (this.vendorMsgListener) {
            this.SubscribeOnFullLoging(this.vendorMsgListener);
        }
        else {
            this.UnSubscribeOnFullLoging();
        }
        const dc = this.dataCache;
        const qc = dc.FQuoteCache;
        vendor.Test = _Connection.Test++;
        qc.onRequestQuotesSubscription.Subscribe(vendor.SubscribeSymbol, vendor);
        qc.onCancelQuotesSubscription.Subscribe(vendor.UnsubscribeSymbol, vendor);
        vendor.AddMessageListener(dc.NewMessage, dc);
        vendor.AddConnectedListener(this.OnVendorConnected, this);
        vendor.AddDisconnectedListener(this.OnVendorDisconnected);
        vendor.AddConnectionLostListener(this.OnConnectionLost, this);
        vendor.AddOnErrorListener(this.OnError, this);
        return vendor;
    }
    _connectProcess(vendor, connectParams) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._localDataProvider) {
                const { ConnectionLocalDataProvider } = yield import('@shared/commons/ConnectionLocalDataProvider');
                this._localDataProvider = new ConnectionLocalDataProvider();
            }
            const instrumentIds = yield this._localDataProvider.getInstrumentIds(connectParams.login, connectParams.connectionName);
            const enrichedConnectParams = Object.assign(Object.assign({}, connectParams), { instrumentIds });
            const connectResultData = yield vendor.Connect(enrichedConnectParams);
            const data = {};
            this.Reconnecting = false;
            data.ConnectParams = enrichedConnectParams;
            data.ConnectResultData = connectResultData;
            if (connectResultData.accessTokenLifeTime) {
                if (this.RefreshAccessTokenInterval) {
                    clearInterval(this.RefreshAccessTokenInterval);
                }
                this.RefreshAccessTokenInterval = null;
                const timeout = (connectResultData.accessTokenLifeTime - 15) * 1000;
                this.RefreshAccessTokenInterval = setTimeout(this.RefreshAccessToken.bind(this), timeout);
            }
            this.ConnectResultData = data;
            if (!isNullOrUndefined(connectResultData.accessToken) && !connectResultData.isPasswordExpired) {
                this.onGetToken.Raise(this);
            }
            return connectResultData.Connected;
        });
    }
    OnVendorConnected(connectedArguments) {
        if (connectedArguments.isPasswordExpired) {
            this.lastConnectionAttemptResponse = {
                text: '',
                UserID: connectedArguments.UserID,
                isPasswordExpired: connectedArguments.isPasswordExpired,
                ConnectReason: connectedArguments.ConnectionStateCode
            };
            void this.SetConnectionState(ConnectionStates.HALFCONNECTED);
        }
        else {
            void this.SetConnectionState(ConnectionStates.CONNECTED);
        }
    }
    disconnect(isNoNActivity = false) {
        if (this.funcBeforeDisconnectCallback) {
            this.funcBeforeDisconnectCallback();
        }
        this.Reconnecting = false;
        if (this.vendor) {
            void this.vendor.Disconnect({ isNoNActivity });
        }
    }
    ClearPreviousVendorRefs() {
        const vendor = this.vendor;
        const dc = this.dataCache;
        const qc = dc.FQuoteCache;
        dc.cleanup(); // не сможем отписаться от котировок (например в алертах) во время очистки dataCache если до этого отпишемся от vendor.UnsubscribeSymbol
        qc.onRequestQuotesSubscription.UnSubscribe(vendor.SubscribeSymbol, vendor);
        qc.onCancelQuotesSubscription.UnSubscribe(vendor.UnsubscribeSymbol, vendor);
        vendor.RemoveMessageListener(dc.NewMessage, dc);
        vendor.RemoveConnectedListener(this.OnVendorConnected, this);
        vendor.RemoveDisconnectedListener(this.OnVendorDisconnected);
        vendor.RemoveConnectionLostListener(this.OnConnectionLost, this);
        vendor.RemoveOnErrorListener(this.OnError, this);
    }
    OnError() {
        const disconectArguments = {
            ErrorInConnection: true,
            Text: Resources.getResource('connect.connection.unknown.error')
        };
        void this.OnVendorDisconnected(disconectArguments); // предотвращаем зависание на Connecting
    }
    SubscribeOnErrorMultiPolicy(callback) {
        this._eventEmitter.on('errorMultiPolicy', callback);
    }
    UnsubscribeOnErrorMultiPolicy(callback) {
        this._eventEmitter.off('errorMultiPolicy', callback);
    }
    OnConnectionLost() {
        return __awaiter(this, void 0, void 0, function* () {
            this.Reconnecting = true;
            if (this.RefreshAccessTokenInterval) {
                clearInterval(this.RefreshAccessTokenInterval);
            }
            this.RefreshAccessTokenInterval = null;
            yield this.SetConnectionState(ConnectionStates.CONNECTION_LOST);
        });
    }
    SetConnectionState(state_1) {
        return __awaiter(this, arguments, void 0, function* (state, forse = false) {
            const needEvent = this.connectionState !== state;
            this.connectionState = state;
            if (needEvent || forse) {
                try {
                    yield this.onConnectStateChange();
                }
                catch (e) {
                    ErrorInformationStorage.GetException(e);
                }
            }
        });
    }
    getState() {
        return this.connectionState;
    }
    isDisconnected() {
        return this.connectionState === ConnectionStates.DISCONNECTED;
    }
    isConnectionCorrect() {
        return (this.connectionState !== ConnectionStates.DISCONNECTED &&
            this.connectionState !== ConnectionStates.CONNECTION_LOST);
    }
    getPackIn() {
        if (this.vendor) {
            return this.vendor.in;
        }
        else {
            return 0;
        }
    }
    getPackOut() {
        if (this.vendor) {
            return this.vendor.out;
        }
        else {
            return 0;
        }
    }
    SubscribeOnFullLoging(handler) {
        var _a;
        if ((_a = this.vendor) === null || _a === void 0 ? void 0 : _a.SubscribeOnFullLoging) {
            this.vendor.SubscribeOnFullLoging(handler);
        }
        this.vendorMsgListener = handler;
    }
    UnSubscribeOnFullLoging() {
        var _a;
        if ((_a = this.vendor) === null || _a === void 0 ? void 0 : _a.UnSubscribeOnFullLoging) {
            this.vendor.UnSubscribeOnFullLoging();
        }
        this.vendorMsgListener = null;
    }
    GetMessagesList() {
        // TODO
        return Message.CodesString;
        // if (this.vendor && this.vendor.GetMessagesList)
        //    return this.vendor.GetMessagesList();
    }
    SendPing() {
        const startTime = new Date().getTime();
        if (this.vendor) {
            return this.vendor.SendPing().then(function () {
                const finishTime = new Date().getTime();
                const diff = finishTime - startTime;
                return diff;
            });
        }
    }
    RefreshAccessToken(fromReconnect) {
        if (!this.ConnectResultData) {
            return Promise.resolve(false);
        }
        return this.vendor
            .RefreshAccessToken(this.ConnectResultData, fromReconnect)
            .then(function (TokenDataObj) {
            if (!TokenDataObj.StreamOpened) {
                return TokenDataObj;
            }
            if (!TokenDataObj.Valid) {
                return TokenDataObj;
            }
            const data = this.ConnectResultData;
            const CP = data.ConnectParams;
            const CRD = data.ConnectResultData;
            CP.accessToken = CRD.accessToken = TokenDataObj.accessToken;
            CRD.accessTokenLifeTime = TokenDataObj.accessTokenLifeTime;
            CRD.refreshToken = TokenDataObj.refreshToken;
            CRD.refreshTokenLifeTime = TokenDataObj.refreshTokenLifeTime;
            // value in seconds
            if (TokenDataObj.accessTokenLifeTime && !this.Reconnecting) {
                // 15 seconds before
                let timeout = (TokenDataObj.accessTokenLifeTime - 15) * 1000;
                // anti Ira defence
                if (timeout >= 2147483647) {
                    // limits it to (2^31)-1 ms (2147483647 ms)
                    timeout = 2147483640;
                } // well, to be sure
                this.RefreshAccessTokenInterval = setTimeout(this.RefreshAccessToken.bind(this), timeout);
            }
            return { Valid: true, StreamOpened: true };
        }.bind(this))
            .catch(function () {
            console.log('Refresh Access Token FAILED!!!!!!!');
            return { Valid: false, StreamOpened: false, FatalError: true };
        });
    }
    AccountOperationRequest(data) {
        if (this.vendor) {
            return this.vendor.AccountOperationRequest(data);
        }
        return Promise.resolve(null);
    }
    ChangePassword(curPwd, newPwd, UserID, verificationPassword) {
        var _a;
        const accessToken = ((_a = this.ConnectResultData) === null || _a === void 0 ? void 0 : _a.ConnectParams)
            ? this.ConnectResultData.ConnectParams.accessToken
            : '';
        if (this.vendor) {
            return this.vendor.ChangePassword(curPwd, newPwd, UserID, verificationPassword, accessToken);
        }
        return Promise.resolve(null);
    }
    ChangeTradingPassword(curPwd, newPwd) {
        if (this.vendor) {
            return this.vendor.ChangeTradingPassword(curPwd, newPwd);
        }
        return Promise.resolve(null);
    }
    SendSOAPRequest(request, connectTo) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield PFSVendor.SendSOAPRequest(request, connectTo, this.vendorMsgListener);
        });
    }
    // msgType - name of method. like SendRequestRecoveryPassword, SendPasswordRecoveryChangeRequest etc
    SendMessageToVendor(url, isSSL, msgType, dataForMsg, mode) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield PFSVendor.SendMessageToVendor(url, isSSL, msgType, dataForMsg, this.vendorMsgListener, mode);
        });
    }
    sendOutputMessageAsync(message) {
        return __awaiter(this, void 0, void 0, function* () {
            const vendor = this.vendor;
            if (isNullOrUndefined(vendor)) {
                return yield Promise.resolve(null);
            }
            return yield vendor.sendOutputMessageAsync(message);
        });
    }
    // #region Vendor Factory
    createPFSVendor(tradingValidator) {
        const vendor = new PFSVendor();
        vendor.tradingValidator = tradingValidator;
        return vendor;
    }
    // #endregion Vendor Factory
    GetHistoryPromise(historyParams, signal) {
        return __awaiter(this, void 0, void 0, function* () {
            const vndr = this.vendor;
            if (!vndr) {
                return [];
            }
            const intervals = yield vndr.GetHistoryPromise(historyParams, this.ConnectResultData, signal);
            return intervals;
        });
    }
    getErrorCode() {
        return this.lastConnectionAttemptResponse.ErrorCode;
    }
    getLocalizedConnectionErrorString(LoginName) {
        let rawString = this.lastConnectionAttemptResponse.text;
        const codeError = this.lastConnectionAttemptResponse.ErrorCode;
        const JSONParsed = tryParseJson(rawString);
        if (JSONParsed === null || JSONParsed === void 0 ? void 0 : JSONParsed.message) {
            rawString = JSONParsed.message;
            // code = rawString.code || null
        } /* else if (isValidString(rawString)) {
            ErrorInformationStorage.GetException(new CustomErrorClass(rawString, 'NonJSONError', ''));
        } */
        if (rawString === 'password.expired') {
            return '';
        }
        if (rawString === 'additional.verification') {
            return Resources.getResource('screen.licensing.statusMessages.connecting');
        }
        let locString = '';
        locString = Resources.getResource(rawString);
        const attIndex = rawString.indexOf('Attempt');
        if (attIndex !== -1) {
            const attC = rawString.substring(attIndex + 8).trim();
            locString = Resources.getResource('general.connection.Login/password combination is not valid. Attempt').replace('{0}', attC);
        }
        else {
            let tmpRawString = 'general.connection.' + rawString;
            if (Resources.IsResourcePresent(tmpRawString)) {
                locString = Resources.getResource(tmpRawString);
            }
            else {
                tmpRawString = 'general.connection.' + rawString + '.';
                if (Resources.IsResourcePresent(tmpRawString)) {
                    locString = Resources.getResource(tmpRawString);
                }
            }
        }
        if (JSONParsed === null || JSONParsed === void 0 ? void 0 : JSONParsed.code) {
            const code = parseInt(JSONParsed.code);
            switch (code) {
                case _Connection.ERROR_LOCKED_AFTER_BRUTEFORCE_PERMANENT:
                    locString = Resources.getResource('User is locked after brute force!');
                    break;
                case _Connection.ERROR_LOCKED_AFTER_BRUTEFORCE_TIME:
                    locString = Resources.getResource('screen.LoginScreen.responceErrors.bruteforceBlockedMinutes')
                        .replace('{1}', LoginName)
                        .replace('{2}', JSONParsed.minutes);
                    break;
                case _Connection.ERROR_LOCKED_BACKOFFICE:
                    locString = Resources.getResource('User is blocked. Please contact support.');
                    break;
            }
        }
        if (codeError === ConnnectionResponseCode.SAFE_DISCONECT) {
            locString = Resources.getResource('general.connection.safeDisconect');
        }
        return locString;
    }
    subscribeOnConnectStateChange(callback) {
        this._eventEmitter.on('connectStateChange', callback);
    }
    unsubscribeOnConnectStateChange(callback) {
        this._eventEmitter.off('connectStateChange', callback);
    }
    onConnectStateChange() {
        return __awaiter(this, void 0, void 0, function* () {
            const listeners = this._eventEmitter.listeners('connectStateChange');
            const promises = listeners.map((listener) => listener(this));
            yield Promise.all(promises.map((p) => __awaiter(this, void 0, void 0, function* () {
                p instanceof Promise ? yield p : yield Promise.resolve();
            })));
        });
    }
}
_Connection.Test = 1;
/// <summary>
/// пользователь заблокирован после брутфорс атаки, время до авторазблокировки НЕ доступно (авторазблокировка не отработает) - обычно после рестарта
/// </summary>
_Connection.ERROR_LOCKED_AFTER_BRUTEFORCE_PERMANENT = 195;
/// <summary>
/// пользователь заблокирован после брутфорс атаки, время до авторазблокировки доступно
/// </summary>
_Connection.ERROR_LOCKED_AFTER_BRUTEFORCE_TIME = 215;
/// <summary>
/// у пользователя установлена блокировка Locked в БО
/// </summary>
_Connection.ERROR_LOCKED_BACKOFFICE = 214;
export let Connection = null; // NOSONAR - mutable singleton reference initialized at runtime
export function ConnectionInstanceCreator(dataCache, funcBeforeDisconnectCallback = undefined) {
    Connection = new _Connection(dataCache, funcBeforeDisconnectCallback);
}
